import { Loading } from "assets/icons/Common";
import { DashboardContext } from "contexts/DashboardContext";
import { useContext, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { PhotoProvider } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import styled from "styled-components";
import { WidgetSettingFC } from "types/Setting";
import { formatFileSize } from "util/common";
import { handleImageUpload } from "util/wp-api";

const sizeLimit = 10 * 1024 * 1024;

export interface activeImage {
  imgurl: string;
  widgetId: string;
}

interface ImageWidgetData {
  images: any[];
  activeImages: activeImage[] | undefined;
}
interface Props {}

export const ImageLibrary: WidgetSettingFC<ImageWidgetData, Props> = ({
  data,
  setData,
  state: { activeWidgetId },
}) => {
  const {
    data: { images, activeImages },
    setData: updateGlobalData,
  } = useContext(DashboardContext);
  const [files, setFiles] = useState<any[]>(images || []);
  const [activeFiles, setActiveFiles] = useState<activeImage[] | undefined>(
    activeImages || undefined,
  );
  const [activeFile, setActiveFile] = useState<activeImage | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setActiveFile(activeFiles?.find((item) => item.widgetId === activeWidgetId));
  }, [activeWidgetId, activeFiles]);

  const handleUpload = async (file: any) => {
    if (file) {
      try {
        const url = await handleImageUpload(file); // Use the imported function
        return url;
      } catch (error) {
        console.error("Image upload failed:", error);
      }
    } else {
      console.error("Please select a file first.");
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
    },
    onDrop: async (acceptedFiles, fileRejections) => {
      const validFiles = acceptedFiles.filter((file) => {
        if (file.size > sizeLimit) {
          alert(
            `${file.name} is too large, please select a file smaller than ${formatFileSize(sizeLimit)}`,
          );
          return false;
        }
        return true;
      });

      setLoading(true);

      try {
        // Use Promise.all to wait for all uploads to finish
        const uploadPromises = validFiles.map(async (file) => {
          try {
            const uploadedFile = await handleUpload(file);
            return uploadedFile;
          } catch (uploadError) {
            console.error(`Failed to upload ${file.name}:`, uploadError);
            return null;
          }
        });

        const uploadedFiles = await Promise.all(uploadPromises);

        // Filter out any failed uploads (null values)
        const successfulUploads = uploadedFiles.filter((file) => file !== null);

        if (successfulUploads.length > 0) {
          // Update state and global data with all successfully uploaded files
          setFiles((prevFiles) => [...prevFiles, ...successfulUploads]);
          updateGlobalData({ images: [...files, ...successfulUploads] });
        } else {
          console.error("No files were successfully uploaded");
        }
      } catch (error) {
        console.error("Error uploading files:", error);
      } finally {
        setLoading(false); // Set loading to false after uploads are complete
      }

      const rejectedFiles = fileRejections.map((file) => file.file.name).join(", ");

      if (rejectedFiles) {
        alert(`The following files are not supported: ${rejectedFiles}`);
      }
    },
  });

  const openImage = (imgurl: string) => {
    const newActiveFiles = (activeFiles || []).filter((item) => item.widgetId !== activeWidgetId);
    setActiveFiles([...newActiveFiles, { imgurl, widgetId: activeWidgetId }]);
    updateGlobalData({ activeImages: [...newActiveFiles, { imgurl, widgetId: activeWidgetId }] });
    setActiveFile({ imgurl, widgetId: activeWidgetId });
  };

  return (
    <PhotoProvider>
      <section
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            marginTop: 20,
            marginBottom: 32,
            border: "5px dotted var(--widget-primary)",
            height: 150,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            alignSelf: "flex-end",
            cursor: "pointer",
          }}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <p style={{ textAlign: "center" }}>
            Drag 'n' drop some files here, or click to select files
          </p>
        </div>
        <div
          style={{ display: "flex", gap: 16, marginBottom: 10, flexWrap: "wrap", width: "100%" }}
        >
          {files.length > 0 &&
            files.map((item: any, index: number) => {
              const imgurl = item;
              const active = activeFile?.widgetId === activeWidgetId && activeFile?.imgurl === item;
              return (
                <div
                  key={index}
                  style={{
                    height: 74,
                    width: 74,
                    border: active
                      ? "2px solid var(--widget-primary)"
                      : "1px solid var(--widget-primary)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    borderRadius: 8,
                  }}
                  onClick={() => openImage(imgurl)}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      backgroundColor: "white",
                      height: 20,
                      width: 20,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: 10,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Create a new array excluding the element at the current index
                      const newFiles = files.filter((item) => item !== imgurl);
                      const newActiveFiles = activeFiles?.filter((item) => item.imgurl !== imgurl);
                      setFiles(newFiles);
                      setActiveFiles(newActiveFiles);
                      updateGlobalData({ images: newFiles, activeImages: newActiveFiles });
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      xmlSpace="preserve"
                      width={10}
                      height={10}
                      viewBox="0 0 490 490"
                    >
                      <path d="M456.851 0 245 212.564 33.149 0 .708 32.337l211.961 212.667L.708 457.678 33.149 490 245 277.443 456.851 490l32.441-32.322-211.961-212.674L489.292 32.337z" />
                    </svg>
                  </div>
                  <img
                    src={imgurl}
                    alt="img"
                    height={"80%"}
                    width={"80%"}
                    style={{ objectFit: "contain" }}
                  ></img>
                </div>
              );
            })}
        </div>
        {loading && (
          <LoadingIcon>
            <Loading />
          </LoadingIcon>
        )}
      </section>
    </PhotoProvider>
  );
};

const LoadingIcon = styled.div`
  width: 75px;
  height: 75px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--widget-primary);

  & > svg {
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @-webkit-keyframes spin {
    0% {
      -webkit-transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
